import { QuoteResultDto } from 'apiConnector/quote/dto/quoteResultDto';
import { bookingRequestApi } from 'apiConnector/quote/dynamicBooking';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { BookingRequestPayload } from 'epic/common-form/Booking';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics/tabRemoved';
import { DynamicFormResult } from 'models/forms/DynamicFormResult';
import { ofType } from 'redux-observable';
import { EMPTY, Observable, of, type SchedulerLike, throwError } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  mergeWith,
  startWith,
  take,
  takeUntil,
  timeout,
  withLatestFrom,
} from 'rxjs/operators';
import { FunkyAction } from 'shared/middlewares/epic/epicMiddleware.factory';
import { dynamicBookingTradeDone, dynamicBookingTradeRejected } from 'state/dynamic/dynamicForm.action';
import * as actionTypes from 'state/dynamic/dynamicForm.actionTypes';
import {
  dynamicBookingAckedThunk,
  dynamicBookingRequestFailedThunk,
  dynamicBookingTradeAuthFailureReceivedThunk,
  dynamicTradeAuthReceivedThunk,
  quoteResultFailedForBookingThunk,
  quoteResultReceivedForBookingThunk,
} from 'state/dynamic/dynamicForm.thunks';
import { DynamicFormInfos } from 'state/dynamic/infos/infos.model';
import { commonQuoteAction } from 'state/forms/actions/quote';
import { commonTradeAction } from 'state/forms/actions/trade';
import { NextTradeAuthorizationResult, TradeExecReport } from 'state/quotes/trades/trades.model';
import type { EpicWithThunk } from 'state/state.common';
import { AppState } from 'state/state.model';
import { IHttpClient } from 'utils/http.old/httpClient.model';
import { IFrontLogger } from 'utils/loggers/FrontLogger';
import { fatEventApiConnector } from 'utils/streaming/api';
import { dynamicQuoteResult, tradeAuthorization, tradeExecutionReport } from 'utils/streaming/messages';
import { Streaming } from 'utils/streaming/model';

export function requestBookingEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (
    action$,
    state$,
    { oldHttpClient, streaming, getNow, frontLogger, selectors: { getDynamicFormInfos }, getSgmeConfiguration },
  ) =>
    action$.pipe(
      ofType(actionTypes.DYNAMIC_BOOKING_REQUESTED),
      withLatestFrom(state$),
      mergeMap(([{ tabId, quoteId, frontReconciliationId, withExecution }, state]) => {
        const bookingReportTimeoutMs = getSgmeConfiguration().bookingReportTimeoutMs;

        const bookingRequest = getBookingRequest(
          {
            tabId,
            frontReconciliationId,
            quoteId,
          },
          state,
          getDynamicFormInfos,
        );
        const bookingAckTimeoutMs = getSgmeConfiguration().bookingAckTimeoutMs;
        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        const quotePushback$ = quotePushbackStream(streaming, oldHttpClient, getNow, frontReconciliationId, tabId).pipe(
          timeout({
            first: bookingReportTimeoutMs,
            scheduler: appScheduler,
            with: () =>
              throwError(() =>
                commonQuoteAction.quoteResultFailed(
                  'dynamic',
                  tabId,
                  quoteId,
                  {
                    message: 'quote.errorMessages.responseTimeout',
                    values: { timeInMillis: bookingReportTimeoutMs },
                  },
                  getNow(),
                ),
              ),
          }),
        );
        const tradeAuthorisation$ = tradeAuthorisationStream(quoteId, tabId, streaming, oldHttpClient).pipe(
          timeout({
            first: bookingReportTimeoutMs,
            scheduler: appScheduler,
            with: () =>
              throwError(() =>
                commonQuoteAction.quoteResultFailed(
                  'dynamic',
                  tabId,
                  quoteId,
                  {
                    message: 'quote.errorMessages.responseTimeout',
                    values: { timeInMillis: bookingReportTimeoutMs },
                  },
                  getNow(),
                ),
              ),
          }),
        );
        const executionReport$ = executionReportStream(quoteId, tabId, streaming, oldHttpClient, getNow).pipe(
          timeout({
            first: bookingReportTimeoutMs,
            scheduler: appScheduler,
            with: () =>
              throwError(() =>
                commonQuoteAction.quoteResultFailed(
                  'dynamic',
                  tabId,
                  quoteId,
                  {
                    message: 'quote.errorMessages.responseTimeout',
                    values: { timeInMillis: bookingReportTimeoutMs },
                  },
                  getNow(),
                ),
              ),
          }),
        );
        const allStreams$ = quotePushback$.pipe(
          mergeWith(tradeAuthorisation$, executionReport$),
          catchError((errorAction: FunkyAction) => of(errorAction)),
        );

        return bookingRequestApi(oldHttpClient, bookingAckTimeoutMs)
          .requestBooking(bookingRequest, withExecution)
          .pipe(
            takeUntil(tabRemoved$),
            map(bookingAck =>
              dynamicBookingAckedThunk(bookingAck, { tabId, withExecution, frontReconciliationId, quoteId }),
            ),
            startWith(commonTradeAction.quoteTradeSent('dynamic', tabId, quoteId)),
            mergeWith(allStreams$),
            catchError((error: Error) =>
              handleBookingRequestError(
                {
                  tabId,
                  frontReconciliationId,
                },
                error,
                frontLogger,
              ),
            ),
          );
      }),
    );
}

function getBookingRequest(
  {
    tabId,
    frontReconciliationId,
    quoteId,
  }: {
    tabId: string;
    frontReconciliationId: string;
    quoteId: string;
  },
  state: AppState,
  getDynamicFormInfos: (state: AppState, tabId: string) => DynamicFormInfos | undefined,
): BookingRequestPayload {
  const { quoteQuery, negotiationId, version, instrumentName, instrumentContractName } = getDynamicFormInfos(
    state,
    tabId,
  )!;
  return {
    negotiationId,
    quoteId: quoteId,
    version,
    quoteQuery,
    instrumentName,
    instrumentContractName,
    reconciliationId: frontReconciliationId,
  };
}

function handleBookingRequestError(
  {
    tabId,
    frontReconciliationId,
  }: {
    tabId: string;
    frontReconciliationId: string;
  },
  error: Error,
  frontLogger: IFrontLogger,
) {
  frontLogger.error('An error occurred during dynamic booking request {@FrontError}', error);
  return of(
    dynamicBookingRequestFailedThunk(
      tabId,
      apiErrorHelper.toApiErrorMessage(error, { timeoutErrorMessage: 'booking.errorMessages.timeout' }),
      frontReconciliationId,
    ),
  );
}

function quotePushbackStream(
  streaming: Streaming,
  oldHttpClient: IHttpClient,
  getNow: () => Date,
  frontReconciliationId: string,
  tabId: string,
): Observable<FunkyAction> {
  return dynamicQuoteResult(streaming).pipe(
    filter(event => event.reconciliationId === frontReconciliationId),
    mergeMap(lightEvent =>
      fatEventApiConnector(oldHttpClient)
        .getFatEventWithTabId<QuoteResultDto<DynamicFormResult>>(lightEvent, tabId)
        .pipe(
          catchError(() =>
            throwError(() =>
              commonQuoteAction.quoteResultFailed(
                'dynamic',
                tabId,
                lightEvent.quoteId!,
                { message: 'quote.errorMessages.TechnicalError' },
                getNow(),
              ),
            ),
          ),
          mergeMap(event => {
            return handleQuotePushback(tabId, lightEvent.quoteId!, event);
          }),
        ),
    ),
    take(1),
  );
}

function handleQuotePushback(tabId: string, quoteId: string, event: QuoteResultDto<DynamicFormResult>) {
  if (event.status === 'Quoted') {
    return of(quoteResultReceivedForBookingThunk(tabId, quoteId, event));
  } else {
    return throwError(() => quoteResultFailedForBookingThunk(tabId, quoteId, event));
  }
}
function tradeAuthorisationStream(
  quoteId: string,
  tabId: string,
  streaming: Streaming,
  oldHttpClient: IHttpClient,
): Observable<FunkyAction> {
  return tradeAuthorization(streaming).pipe(
    filter(tradeAuth => tradeAuth.quoteId === quoteId),
    mergeMap(lightEvent =>
      fatEventApiConnector(oldHttpClient)
        .getFatEvent<NextTradeAuthorizationResult>(lightEvent)
        .pipe(
          catchError(_ => EMPTY),
          mergeMap(tradeAuth => handleTradeAuthorization(tabId, quoteId, tradeAuth)),
        ),
    ),
    take(1),
  );
}

function handleTradeAuthorization(tabId: string, quoteId: string, tradeAuth: NextTradeAuthorizationResult) {
  if (tradeAuth.status === 'Granted') {
    return of(dynamicTradeAuthReceivedThunk(tabId, quoteId, tradeAuth));
  } else {
    return throwError(() => dynamicBookingTradeAuthFailureReceivedThunk(tabId, quoteId, tradeAuth));
  }
}

function executionReportStream(
  quoteId: string,
  tabId: string,
  streaming: Streaming,
  oldHttpClient: IHttpClient,
  getNow: () => Date,
): Observable<FunkyAction> {
  return tradeExecutionReport(streaming).pipe(
    filter(r => r.quoteId === quoteId),
    mergeMap(lightEvent =>
      fatEventApiConnector(oldHttpClient)
        .getFatEvent<TradeExecReport>(lightEvent)
        .pipe(
          catchError(_ => EMPTY),
          mergeMap(report => handleTradeReport(tabId, quoteId, report, getNow())),
        ),
    ),
    take(1),
  );
}

function handleTradeReport(
  tabId: string,
  quoteId: string,
  { status, errors }: TradeExecReport,
  when: Date,
): Observable<FunkyAction> {
  switch (status) {
    case 'FULLY_EXECUTED':
      return of(dynamicBookingTradeDone(tabId, quoteId, status, when));
    case 'CANCELLED':
    case 'EXPIRED':
    case 'FAILED':
    case 'REJECTED':
      return throwError(() => dynamicBookingTradeRejected(tabId, quoteId, errors!, when));
    default:
      return EMPTY;
  }
}
